import { useMemo, useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { 
  ChevronLeft, 
  Search, 
  Users, 
  GraduationCap, 
  ArrowRight,
  Loader2,
  CheckCircle2,
  User,
  Mail,
  Phone,
  MapPin,
  ShieldCheck,
  Building2,
  Clock,
  CreditCard
} from "lucide-react";
import PolicyDetailsHeader from "../../components/payments/PolicyDetailsHeader";
import MobileMoneyForm from "../../components/payments/MobileMoneyForm";
import IntercityTravelForm from "../../components/payments/IntercityTravelForm";
import OkadaForm from "../../components/payments/OkadaForm";
import TaxiForm from "../../components/payments/TaxiForm";
import ReviewStep from "../../components/payments/ReviewStep";
import clientService from "../../api/ClientService";
import { mapClientData } from "../../utils/clientHelpers";
import Config from "../../utils/Config";
import useAccountStatusCheck from "../../hooks/useAccountStatusCheck";
import { formatMoney } from "../../utils/formatMoney";
import Loader from "../../components/misc/Loader";

const METHODS = [
  { key: "momo", label: "Mobile Money" },
];

// Client/Student Card Component
function ClientCard({ client, isSelected, onClick, type = "client" }) {
  const fullName = client.fullName || `${client.firstName || ""} ${client.lastName || ""}`.trim();
  const displayPhone = client.phone || client.phone_number || "";
  const displayEmail = client.email || "";

  return (
    <motion.button
      whileHover={{ y: -2, scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`relative rounded-xl border text-left shadow-sm transition-all duration-300 focus:outline-none w-full ${
        isSelected
          ? "ring-2 ring-emerald-500 bg-emerald-50 border-emerald-300"
          : "ring-1 ring-gray-200 bg-white hover:shadow-lg hover:ring-2 hover:ring-emerald-300"
      }`}
    >
      <div className="p-4 flex gap-3 items-center">
        <div className={`flex-shrink-0 flex justify-center items-center w-12 h-12 rounded-full ${
          isSelected ? "bg-emerald-500" : type === "student" ? "bg-blue-100" : "bg-gray-100"
        }`}>
          {isSelected ? (
            <CheckCircle2 className="w-6 h-6 text-white" />
          ) : type === "student" ? (
            <GraduationCap className="w-6 h-6 text-blue-600" />
          ) : (
            <User className="w-6 h-6 text-gray-600" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className={`text-base font-semibold truncate ${
            isSelected ? "text-emerald-900" : "text-gray-900"
          }`}>
            {fullName}
          </h4>
          <div className="mt-1 space-y-0.5">
            {displayPhone && (
              <p className="text-sm text-gray-600 truncate flex items-center gap-1">
                <Phone size={12} /> {displayPhone}
              </p>
            )}
            {displayEmail && (
              <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                <Mail size={12} /> {displayEmail}
              </p>
            )}
            {type === "student" && client.schoolName && (
              <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                <GraduationCap size={12} /> {client.schoolName}
              </p>
            )}
            {(client.region || client.location) && (
              <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                <MapPin size={12} /> {[client.region, client.location].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
        </div>

        {isSelected && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="flex-shrink-0"
          >
            <div className="flex justify-center items-center w-6 h-6 bg-emerald-500 rounded-full">
              <CheckCircle2 className="w-4 h-4 text-white" />
            </div>
          </motion.div>
        )}
      </div>
    </motion.button>
  );
}

export default function Checkout() {
  const { state } = useLocation();
  const navigate = useNavigate();
  
  // Check account status
  const { isBlocked, AccountStatusModal } = useAccountStatusCheck(true, () => {
    navigate("/dashboard");
  });

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  
  // Client/Student selection
  const [clientType, setClientType] = useState(null); // "clients" or "students"
  const [selectedClient, setSelectedClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [students, setStudents] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  
  // Search and pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 9;

  // Payment and form states
  const [method, setMethod] = useState("momo");
  const [submitting, setSubmitting] = useState(false);
  const [additionalDetails, setAdditionalDetails] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch clients
  const fetchClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const response = await clientService.getClients();
      if (response.data?.status_code === 200 && Array.isArray(response.data.data)) {
        const mapped = response.data.data.map(mapClientData);
        setClients(mapped);
      } else {
        toast.error(response.data?.message || "Failed to fetch clients");
        setClients([]);
      }
    } catch (error) {
      toast.error("An error occurred while fetching clients");
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  }, []);

  // Fetch students
  const fetchStudents = useCallback(async () => {
    setLoadingStudents(true);
    try {
      const response = await clientService.getStudents();
      if (response.status && Array.isArray(response.data)) {
        const mapped = response.data.map((s) => {
          const status = s.account_status === 1 ? "Active" : s.account_status === 0 ? "Pending" : "Inactive";
          return {
            ...s,
            id: s.slug || s.id || Math.random(),
            slug: s.slug,
            firstName: s.first_name || "",
            lastName: s.last_name || "",
            fullName: `${s.first_name || ""} ${s.last_name || ""}`.trim(),
            email: s.email || "",
            phone: s.phone_number || "",
            phone_number: s.phone_number || "",
            region: s.region || "",
            location: s.location || "",
            schoolName: s.school_name || "",
            studentId: s.student_school_id || "",
            status,
          };
        });
        setStudents(mapped);
      } else {
        toast.error(response.message || "Failed to fetch students");
        setStudents([]);
      }
    } catch (error) {
      toast.error("An error occurred while fetching students");
      setStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  }, []);

  // Fetch data when client type is selected
  useEffect(() => {
    if (clientType === "clients") {
      fetchClients();
    } else if (clientType === "students") {
      fetchStudents();
    }
  }, [clientType, fetchClients, fetchStudents]);

  // Extract policy data from state
  const { 
    policyType, 
    providerName, 
    coverLabel, 
    amount: initialAmount, 
    coverageDuration, 
    duration, 
    selectedPackage, 
    insuranceType, 
    originalPolicy, 
    insurance_provider_slug: stateInsuranceProviderSlug,
    policySlug: statePolicySlug
  } = useMemo(() => {
    if (state?.policyType || state?.insuranceType) return state;
    const fromSession = sessionStorage.getItem("pendingCheckout");
    if (fromSession) {
      const parsed = JSON.parse(fromSession);
      sessionStorage.removeItem("pendingCheckout");
      return parsed;
    }
    return {
      policyType: "daily_commuter",
      providerName: "",
      coverLabel: "2 Day Cover",
      amount: 0,
      coverageDuration: null,
      duration: null,
      selectedPackage: null,
      insuranceType: null,
      originalPolicy: null,
      insurance_provider_slug: null,
      policySlug: null,
    };
  }, [state]);

  const isTravelInsuranceWithBothTypes =
    insuranceType === "travel_insurance" && policyType === "travel_insurance";
  const isShortCheckout =
    insuranceType === "life_insurance" ||
    insuranceType === "general_insurance" ||
    isTravelInsuranceWithBothTypes;

  // Extract insurance_provider_slug
  const insuranceProviderSlug = useMemo(() => {
    if (stateInsuranceProviderSlug) return stateInsuranceProviderSlug;
    if (state?.insurance_provider_slug) return state.insurance_provider_slug;
    const fromSession = sessionStorage.getItem("pendingCheckout");
    if (fromSession) {
      try {
        const parsed = JSON.parse(fromSession);
        if (parsed.insurance_provider_slug) return parsed.insurance_provider_slug;
      } catch (e) {}
    }
    if (!originalPolicy?.insurance_provider) return null;
    if (typeof originalPolicy.insurance_provider === 'object') {
      return originalPolicy.insurance_provider.slug || originalPolicy.insurance_provider.id || null;
    }
    if (typeof originalPolicy.insurance_provider === 'string') {
      return originalPolicy.insurance_provider;
    }
    return null;
  }, [originalPolicy, state, stateInsuranceProviderSlug]);

  // Calculate amount
  const amount = useMemo(() => {
    if (additionalDetails.amount) return additionalDetails.amount;
    if (coverageDuration && typeof coverageDuration === 'object' && coverageDuration.price) {
      return coverageDuration.price;
    }
    if (initialAmount && initialAmount > 0) return initialAmount;
    if (selectedPackage?.premium) return selectedPackage.premium;
    return 0;
  }, [additionalDetails.amount, coverageDuration, initialAmount, selectedPackage?.premium]);

  // Filter clients/students based on search
  const filteredItems = useMemo(() => {
    const items = clientType === "clients" ? clients : students;
    if (!debouncedSearch.trim()) return items;
    
    const query = debouncedSearch.toLowerCase();
    return items.filter((item) => {
      const fullName = item.fullName || `${item.firstName || ""} ${item.lastName || ""}`.trim();
      return (
        fullName.toLowerCase().includes(query) ||
        (item.firstName || "").toLowerCase().includes(query) ||
        (item.lastName || "").toLowerCase().includes(query) ||
        (item.email || "").toLowerCase().includes(query) ||
        (item.phone || item.phone_number || "").includes(query) ||
        (item.region || "").toLowerCase().includes(query) ||
        (item.location || "").toLowerCase().includes(query) ||
        (item.schoolName || "").toLowerCase().includes(query) ||
        (item.studentId || "").toLowerCase().includes(query)
      );
    });
  }, [clientType, clients, students, debouncedSearch]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredItems.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredItems, currentPage]);

  // Step calculations
  const getTotalSteps = () => {
    if (isShortCheckout) {
      return 4; // Client Type -> Select Client -> Details -> Payment
    }
    return 6; // Client Type -> Select Client -> Details -> Additional Info -> Review -> Payment
  };

  const getStepName = (step) => {
    if (step === 1) return "Client Type";
    if (step === 2) return "Select Client";
    if (isShortCheckout) {
      switch (step) {
        case 3: return "Details";
        case 4: return "Payment";
        default: return "Details";
      }
    }
    switch (step) {
      case 3: return "Details";
      case 4: return "Additional Info";
      case 5: return "Review";
      case 6: return "Payment";
      default: return "Details";
    }
  };

  const getPolicyTypeDisplayName = (policyType, insuranceType) => {
    const generalSubcategoryMap = {
      health_insurance: "Health Insurance",
      home_insurance: "Home Insurance",
      accident_insurance: "Accident Insurance",
      fire_insurance: "Fire Insurance",
      marine_insurance: "Marine Insurance",
      liability_insurance: "Liability Insurance",
    };
    const roadTripTypeMap = {
      daily_commuter: "Daily Commuter",
      intercity_travel: "Intercity Travel",
      okada_rider: "Okada Rider",
      taxi_and_bolt: "Taxi & Bolt Driver",
    };

    // Life insurance (by insuranceType or policyType)
    if (insuranceType === "life_insurance" || policyType === "life_insurance") return "Life Insurance";
    // Travel insurance (by insuranceType or policyType)
    if (insuranceType === "travel_insurance" || policyType === "travel_insurance") return "Travel Insurance";
    // General insurance: use subcategory label or "General Insurance"
    if (insuranceType === "general_insurance" || policyType === "general_insurance" || generalSubcategoryMap[policyType]) {
      return generalSubcategoryMap[policyType] || "General Insurance";
    }
    // Road-trip types (daily commuter, intercity, okada, taxi)
    return roadTripTypeMap[policyType] || "Daily Commuter";
  };

  const getPolicySlug = () => {
    if (statePolicySlug) return statePolicySlug;
    if (state?.policySlug) return state.policySlug;
    const fromSession = sessionStorage.getItem("pendingCheckout");
    if (fromSession) {
      try {
        const parsed = JSON.parse(fromSession);
        return parsed.policySlug;
      } catch (e) {}
    }
    return originalPolicy?.slug || "daily-commuter-default";
  };

  // Handlers
  const handleClientTypeSelect = (type) => {
    setClientType(type);
    setSelectedClient(null);
    setSearchQuery("");
    setCurrentPage(1);
    setCurrentStep(2);
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
  };

  const handleContinueFromSelection = () => {
    if (!selectedClient) {
      toast.error("Please select a client or student");
      return;
    }
    setCurrentStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAdditionalDetailsSubmit = (details) => {
    setAdditionalDetails(details);
    setCurrentStep(5); // Go to Review step
  };

  const handleReviewConfirm = () => {
    setCurrentStep(6); // Go to Payment step
  };

  const handleEditDetails = () => {
    setCurrentStep(4); // Go back to Additional Info step
  };

  const handleSubmit = async (payload) => {
    if (!selectedClient) {
      toast.error("Please select a client or student");
      return;
    }

    if (!insuranceProviderSlug) {
      toast.error("Insurance provider information is missing. Please try again or contact support.");
      setSubmitting(false);
      return;
    }

    setSubmitting(true);
    setShowError(false);
    setErrorMessage("");

    try {
      const apiPayload = {
        phone_number: `233${payload.phone.substring(1)}`,
        network: payload.provider,
        insurance_provider_slug: insuranceProviderSlug,
      };

      // Helper function to convert file to base64
      const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });
      };

      // Include client/student data
      const clientData = {
        firstName: selectedClient.firstName || selectedClient.first_name || "",
        lastName: selectedClient.lastName || selectedClient.last_name || "",
        phone: selectedClient.phone || selectedClient.phone_number || "",
        email: selectedClient.email || "",
        type: clientType, // "clients" or "students"
        slug: selectedClient.slug || selectedClient.id,
      };

      // Include coverage duration information
      const isTravelInsuranceWithBothTypes = insuranceType === "travel_insurance" && policyType === "travel_insurance";
      if (insuranceType === "life_insurance" || insuranceType === "general_insurance" || isTravelInsuranceWithBothTypes) {
        if (selectedPackage) {
          apiPayload.coverage_duration = selectedPackage;
        }
        apiPayload.purchased_policy_data = {
          product_name: selectedPackage?.product_name || coverLabel,
          premium: amount,
          premium_duration: selectedPackage?.premium_duration || "monthly",
          duration: selectedPackage?.duration || duration || "N/A",
          description: selectedPackage?.description || "",
          policy_type: policyType,
          insurance_type: insuranceType,
          insurance_coverage_list: selectedPackage?.insurance_coverage_list || [],
          exclusions: selectedPackage?.exclusions || [],
          eligibility_criteria: selectedPackage?.eligibility_criteria || [],
          claim_process_steps: selectedPackage?.claim_process_steps || [],
          terms: selectedPackage?.terms || "",
          client_data: clientData,
          insurance_provider_slug: insuranceProviderSlug,
        };
      } else {
        if (additionalDetails.coverageDuration) {
          apiPayload.coverage_duration = additionalDetails.coverageDuration;
        } else if (coverageDuration) {
          apiPayload.coverage_duration = coverageDuration;
        }
        
        let routeAmount = amount;
        if (coverageDuration && typeof coverageDuration === 'object' && coverageDuration.price) {
          routeAmount = coverageDuration.price;
        } else if (additionalDetails.amount) {
          routeAmount = additionalDetails.amount;
        }
        
        apiPayload.purchased_policy_data = {
          ...additionalDetails,
          amount: routeAmount,
          client_data: clientData,
        };
      }

      // Include client_data in main payload
      apiPayload.client_data = clientData;

      const policySlug = getPolicySlug();

      // LOG PAYLOAD TO CONSOLE INSTEAD OF MAKING API CALL
      console.log("=== PAYMENT PAYLOAD (AGENT) ===");
      console.log("Policy Slug:", policySlug);
      console.log("Full Payload:", JSON.stringify(apiPayload, null, 2));
      console.log("================================");

      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Simulate success
      const mockTransactionId = `TXN-${Date.now()}`;
      setTransactionId(mockTransactionId);
      setSubmitting(false);
      setCurrentStep(getTotalSteps());
      setShowSuccess(true);
      toast.success("Payment payload logged to console successfully!");
    } catch (error) {
      setSubmitting(false);
      setErrorMessage("An error occurred while processing your payment. Please try again.");
      setShowError(true);
    }
  };

  const handlePrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Account Status Modal */}
      <AccountStatusModal />
      
      {/* Blur and disable content when blocked */}
      <div className={isBlocked ? "pointer-events-none opacity-30 blur-sm transition-all duration-300" : "transition-all duration-300"}>
        <div className="pt-24 lg:pt-28" />
        <div className="px-4 mx-auto max-w-6xl sm:px-6 lg:px-8">
          {/* Header */}
          <div className="overflow-hidden relative p-6 mb-6 text-white bg-gradient-to-r from-emerald-600 to-green-600 rounded-2xl">
            <div className="flex flex-wrap gap-4 justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold">Checkout</h1>
                <p className="mt-1 text-sm text-white/80">
                  {currentStep === 1 
                    ? "Select whether you're purchasing for a regular client or student."
                    : currentStep === 2
                    ? "Select a client or student to proceed with the purchase."
                    : "Securely complete the purchase. The policy will be issued instantly after payment."}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => navigate(-1)}
                  className="px-4 py-2 text-sm font-semibold rounded-xl border bg-white/10 hover:bg-white/20 border-white/20"
                >
                  <ChevronLeft size={16} className="inline mr-1" /> Go Back
                </button>
                <button
                  onClick={() => navigate("/insurance/road-trip")}
                  className="px-4 py-2 text-sm font-semibold text-emerald-700 bg-white rounded-xl hover:bg-emerald-50"
                >
                  Cancel Payment
                </button>
              </div>
            </div>
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full pointer-events-none bg-white/10" />
            <div className="absolute -left-12 -bottom-20 w-56 h-56 rounded-full pointer-events-none bg-white/10" />
          </div>

          {/* Step Indicator */}
          <div className="flex flex-wrap gap-3 items-center p-4 mb-6 bg-white rounded-2xl ring-1 ring-gray-200">
            {Array.from({ length: getTotalSteps() }, (_, index) => {
              const stepNumber = index + 1;
              const stepName = getStepName(stepNumber);
              const isActive = currentStep >= stepNumber;

              return (
                <div key={stepNumber} className="flex items-center">
                  <div className="flex gap-2 items-center">
                    <span
                      className={`inline-flex justify-center items-center w-6 h-6 text-xs font-bold text-white rounded-full ${
                        isActive ? "bg-emerald-600" : "bg-gray-400"
                      }`}
                    >
                      {stepNumber}
                    </span>
                    <span
                      className={`text-sm font-semibold ${
                        isActive ? "text-gray-800" : "text-gray-600"
                      }`}
                    >
                      {stepName}
                    </span>
                  </div>
                  {stepNumber < getTotalSteps() && (
                    <span
                      className={`w-8 h-px ml-3 ${
                        isActive ? "bg-emerald-600" : "bg-gray-200"
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-4">
              {/* Step 1: Select Client Type */}
              {currentStep === 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6 bg-white rounded-2xl ring-1 ring-gray-200"
                >
                  <div className="text-center mb-8">
                    <h3 className="mb-2 text-2xl font-bold text-gray-900">
                      Who is this policy for?
                    </h3>
                    <p className="text-sm text-gray-600">
                      Select whether you're purchasing for a regular client or a student
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 max-w-2xl mx-auto">
                    <motion.button
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleClientTypeSelect("clients")}
                      className="relative p-6 text-left bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl border-2 border-emerald-200 hover:border-emerald-400 transition-all group"
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className="flex justify-center items-center mb-4 w-16 h-16 bg-emerald-500 rounded-full group-hover:scale-110 transition-transform">
                          <Users className="w-8 h-8 text-white" />
                        </div>
                        <h4 className="mb-2 text-xl font-bold text-gray-900">
                          Regular Clients
                        </h4>
                        <p className="text-sm text-gray-600">
                          Purchase insurance for your registered regular clients
                        </p>
                      </div>
                      <div className="absolute top-4 right-4">
                        <ArrowRight className="w-5 h-5 text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleClientTypeSelect("students")}
                      className="relative p-6 text-left bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-200 hover:border-blue-400 transition-all group"
                    >
                      <div className="flex flex-col items-center text-center">
                        <div className="flex justify-center items-center mb-4 w-16 h-16 bg-blue-500 rounded-full group-hover:scale-110 transition-transform">
                          <GraduationCap className="w-8 h-8 text-white" />
                        </div>
                        <h4 className="mb-2 text-xl font-bold text-gray-900">
                          Students
                        </h4>
                        <p className="text-sm text-gray-600">
                          Purchase insurance for your registered students
                        </p>
                      </div>
                      <div className="absolute top-4 right-4">
                        <ArrowRight className="w-5 h-5 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </motion.button>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Select Client/Student */}
              {currentStep === 2 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-6 bg-white rounded-2xl ring-1 ring-gray-200"
                >
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 sm:text-2xl">
                        Select {clientType === "students" ? "Student" : "Client"}
                      </h3>
                      <div className="mt-2 w-28 h-1 bg-emerald-500 rounded" />
                      <p className="mt-2 text-sm text-gray-600">
                        Choose a {clientType === "students" ? "student" : "client"} for this policy purchase
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setCurrentStep(1);
                        setClientType(null);
                        setSelectedClient(null);
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      ← Back
                    </button>
                  </div>

                  {/* Search Input */}
                  <div className="mb-6">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder={`Search ${clientType === "students" ? "students" : "clients"} by name, phone, email...`}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="py-3 pr-4 pl-10 w-full text-sm rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className="absolute right-3 top-1/2 text-gray-400 -translate-y-1/2 hover:text-gray-600"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    {searchQuery && (
                      <p className="mt-2 text-xs text-gray-500">
                        Found {filteredItems.length} {clientType === "students" ? "student" : "client"}{filteredItems.length !== 1 ? "s" : ""}
                      </p>
                    )}
                  </div>

                  {/* Loading State */}
                  {(clientType === "clients" && loadingClients) || (clientType === "students" && loadingStudents) ? (
                    <div className="flex justify-center items-center py-12">
                      <div className="flex flex-col gap-4 items-center">
                        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
                        <p className="text-sm text-gray-600">Loading {clientType === "students" ? "students" : "clients"}...</p>
                      </div>
                    </div>
                  ) : filteredItems.length === 0 ? (
                    <div className="flex flex-col gap-4 justify-center items-center py-12 text-center">
                      <div className="flex justify-center items-center w-16 h-16 bg-gray-100 rounded-full">
                        <Search className="w-8 h-8 text-gray-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          No {clientType === "students" ? "students" : "clients"} found
                        </h3>
                        <p className="mt-1 text-sm text-gray-600">
                          {searchQuery
                            ? "Try adjusting your search terms"
                            : `No ${clientType === "students" ? "students" : "clients"} available. Register one first.`}
                        </p>
                      </div>
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className="px-4 py-2 text-sm font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100"
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Client/Student Grid */}
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {pageItems.map((item) => (
                          <ClientCard
                            key={item.id || item.slug}
                            client={item}
                            isSelected={selectedClient?.id === item.id || selectedClient?.slug === item.slug}
                            onClick={() => handleClientSelect(item)}
                            type={clientType === "students" ? "student" : "client"}
                          />
                        ))}
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="flex flex-col gap-3 justify-between items-center mt-8 sm:flex-row">
                          <p className="text-sm text-gray-600">
                            Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}
                            –{Math.min(currentPage * ITEMS_PER_PAGE, filteredItems.length)} of {filteredItems.length}
                          </p>
                          <div className="flex gap-2 items-center">
                            <button
                              onClick={handlePrev}
                              disabled={currentPage === 1}
                              className="px-3 py-2 text-sm bg-white rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Prev
                            </button>
                            {Array.from({ length: totalPages }).map((_, i) => (
                              <button
                                key={i}
                                onClick={() => setCurrentPage(i + 1)}
                                className={`w-9 h-9 rounded-lg text-sm font-medium border ${
                                  currentPage === i + 1
                                    ? "bg-emerald-500 text-white border-emerald-500"
                                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                                }`}
                              >
                                {i + 1}
                              </button>
                            ))}
                            <button
                              onClick={handleNext}
                              disabled={currentPage === totalPages}
                              className="px-3 py-2 text-sm bg-white rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Continue Button */}
                      {selectedClient && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="pt-6 mt-6 border-t border-gray-200"
                        >
                          <div className="flex gap-3 justify-between items-center">
                            <div className="flex gap-3 items-center">
                              <div className="flex justify-center items-center w-10 h-10 bg-emerald-100 rounded-full">
                                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  Selected: {selectedClient.fullName || `${selectedClient.firstName} ${selectedClient.lastName}`.trim()}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {selectedClient.phone || selectedClient.phone_number} • {selectedClient.email}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={handleContinueFromSelection}
                              className="inline-flex gap-2 items-center px-6 py-3 text-sm font-semibold text-white bg-emerald-600 rounded-xl shadow-md transition-all hover:bg-emerald-700"
                            >
                              Continue <ArrowRight size={16} />
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </>
                  )}
                </motion.div>
              )}

              {/* Step 3: Policy Details */}
              {currentStep === 3 && (
                <motion.div
                  key="details"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-5 bg-white rounded-2xl ring-1 ring-gray-200"
                >
                  <div className="space-y-6">
                    <div className="text-center">
                      <h3 className="mb-2 text-lg font-bold text-gray-900">
                        Policy Summary
                      </h3>
                      <p className="mb-6 text-sm text-gray-600">
                        Review your selected policy details below
                      </p>
                    </div>

                    <PolicyDetailsHeader
                      policyType={getPolicyTypeDisplayName(policyType, insuranceType)}
                      providerName={providerName}
                      coverLabel={(() => {
                        if (coverageDuration && typeof coverageDuration === 'object' && coverageDuration.route_destination) {
                          return coverageDuration.route_destination;
                        }
                        return coverLabel || (selectedPackage?.product_name ? `${selectedPackage.product_name} - ${selectedPackage.duration}` : "Package");
                      })()}
                    />

                    {/* Selected Client Info */}
                    {selectedClient && (
                      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                        <div className="flex gap-3 items-center mb-3">
                          {clientType === "students" ? (
                            <GraduationCap className="w-5 h-5 text-blue-600" />
                          ) : (
                            <User className="w-5 h-5 text-blue-600" />
                          )}
                          <h4 className="font-semibold text-blue-800">
                            {clientType === "students" ? "Student" : "Client"} Information
                          </h4>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-blue-700">Name:</span>
                            <span className="font-medium text-blue-900">
                              {selectedClient.fullName || `${selectedClient.firstName} ${selectedClient.lastName}`.trim()}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-blue-700">Phone:</span>
                            <span className="font-medium text-blue-900">
                              {selectedClient.phone || selectedClient.phone_number}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-blue-700">Email:</span>
                            <span className="font-medium text-blue-900">
                              {selectedClient.email}
                            </span>
                          </div>
                          {clientType === "students" && selectedClient.schoolName && (
                            <div className="flex justify-between">
                              <span className="text-blue-700">School:</span>
                              <span className="font-medium text-blue-900">
                                {selectedClient.schoolName}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-between items-center p-4 bg-white rounded-2xl ring-1 ring-gray-200">
                      <span className="text-sm text-gray-600">Amount to pay</span>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-gray-900">
                          {formatMoney(amount || 0, "₵ ")}
                        </span>
                        {selectedPackage?.premium_duration && (
                          <div className="text-xs text-gray-500 capitalize mt-0.5">
                            per {(() => {
                              const duration = selectedPackage.premium_duration;
                              const durationMap = {
                                'yearly': 'year',
                                'monthly': 'month',
                                'weekly': 'week',
                                'daily': 'day'
                              };
                              return durationMap[duration.toLowerCase()] || duration;
                            })()}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => {
                          setCurrentStep(2);
                          setSelectedClient(null);
                        }}
                        className="px-6 py-3 font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
                      >
                        ← Back
                      </button>
                      <button
                        onClick={() => {
                          if (isShortCheckout) {
                            setCurrentStep(4); // Go directly to Payment
                          } else {
                            setCurrentStep(4); // Go to Additional Info
                          }
                        }}
                        className="px-6 py-3 font-semibold text-white bg-emerald-600 rounded-xl transition-colors hover:bg-emerald-700"
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Additional Information (for route/vehicle policies) */}
              {currentStep === 4 && !isShortCheckout && 
               Object.keys(additionalDetails).length === 0 && (
                <motion.div
                  key="additional-info"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-5 bg-white rounded-2xl ring-1 ring-gray-200"
                >
                  {policyType === "intercity_travel" && (
                    <IntercityTravelForm
                      onSubmit={handleAdditionalDetailsSubmit}
                      onBack={() => setCurrentStep(3)}
                      initialData={{
                        ...additionalDetails,
                        coverageDuration,
                        originalPolicy,
                      }}
                    />
                  )}
                  {(policyType === "okada_rider" || policyType === "daily_commuter") && (
                    <OkadaForm
                      onSubmit={handleAdditionalDetailsSubmit}
                      onBack={() => setCurrentStep(3)}
                      initialData={additionalDetails}
                      variant={policyType}
                    />
                  )}
                  {policyType === "taxi_and_bolt" && (
                    <TaxiForm
                      onSubmit={handleAdditionalDetailsSubmit}
                      onBack={() => setCurrentStep(3)}
                      initialData={additionalDetails}
                    />
                  )}
                </motion.div>
              )}

              {/* Step 5: Review (for route/vehicle policies) */}
              {currentStep === 5 && !isShortCheckout && 
               Object.keys(additionalDetails).length > 0 && (
                <motion.div
                  key="review"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-5 bg-white rounded-2xl ring-1 ring-gray-200"
                >
                  <ReviewStep
                    policyData={{
                      policyType: getPolicyTypeDisplayName(policyType, insuranceType),
                      providerName,
                      coverLabel: (() => {
                        if (coverageDuration && typeof coverageDuration === 'object' && coverageDuration.route_destination) {
                          return coverageDuration.route_destination;
                        }
                        return coverLabel || (selectedPackage?.product_name ? `${selectedPackage.product_name} - ${selectedPackage.duration}` : "Package");
                      })(),
                      amount,
                    }}
                    additionalDetails={additionalDetails}
                    paymentMethod={method}
                    onEdit={handleEditDetails}
                    onConfirm={handleReviewConfirm}
                  />
                </motion.div>
              )}

              {/* Step 4: Payment (for life, general, and travel insurance with both types) */}
              {currentStep === 4 && isShortCheckout && (
                <motion.div
                  key="payment-daily"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-5 bg-white rounded-2xl ring-1 ring-gray-200"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-900">Payment Details</h3>
                    <button
                      onClick={() => setCurrentStep(3)}
                      className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
                    >
                      ← Back
                    </button>
                  </div>
                  
                  <p className="mb-3 text-sm font-semibold text-gray-800">
                    Choose a payment method
                  </p>
                  <div className="flex gap-2 mb-4">
                    {METHODS.map((m) => (
                      <button
                        key={m.key}
                        onClick={() => setMethod(m.key)}
                        disabled={submitting}
                        className={`px-4 py-2 rounded-xl border text-sm font-medium transition ${
                          method === m.key
                            ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                            : "border-gray-300 bg-white text-gray-800 hover:border-emerald-300"
                        }`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>

                  {method === "momo" ? (
                    <MobileMoneyForm onSubmit={handleSubmit} insuranceProviderSlug={insuranceProviderSlug} />
                  ) : null}

                  {submitting && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="mt-4 text-sm text-gray-600"
                    >
                      Processing payment... Please wait.
                    </motion.div>
                  )}
                </motion.div>
              )}

              {/* Step 6: Payment (for route/vehicle policies after review) */}
              {currentStep === 6 && !isShortCheckout && (
                <motion.div
                  key="payment"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-5 bg-white rounded-2xl ring-1 ring-gray-200"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-900">Payment Details</h3>
                    <button
                      onClick={() => setCurrentStep(5)}
                      className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl transition-colors hover:bg-gray-200"
                    >
                      ← Back
                    </button>
                  </div>
                  
                  <p className="mb-3 text-sm font-semibold text-gray-800">
                    Choose a payment method
                  </p>
                  <div className="flex gap-2 mb-4">
                    {METHODS.map((m) => (
                      <button
                        key={m.key}
                        onClick={() => setMethod(m.key)}
                        disabled={submitting}
                        className={`px-4 py-2 rounded-xl border text-sm font-medium transition ${
                          method === m.key
                            ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                            : "border-gray-300 bg-white text-gray-800 hover:border-emerald-300"
                        }`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>

                  {method === "momo" ? (
                    <MobileMoneyForm onSubmit={handleSubmit} insuranceProviderSlug={insuranceProviderSlug} />
                  ) : null}

                  {submitting && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="mt-4 text-sm text-gray-600"
                    >
                      Processing payment... Please wait.
                    </motion.div>
                  )}
                </motion.div>
              )}

              {/* Tips */}
              <div className="p-5 bg-white rounded-2xl ring-1 ring-gray-200">
                <h4 className="text-base font-semibold text-gray-900">
                  Helpful Tips
                </h4>
                <ul className="mt-2 space-y-2 text-sm text-gray-700">
                  <li>• For Mobile Money, ensure your phone is on and has network.</li>
                  <li>• The policy will be sent instantly after payment.</li>
                  <li>• Make sure the selected {clientType === "students" ? "student" : "client"} information is correct.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 z-50 justify-center items-center p-4 backdrop-blur-sm bg-black/50"
            onClick={() => {
              setShowSuccess(false);
              navigate("/dashboard");
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="p-6 w-full max-w-md bg-white rounded-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-emerald-100 rounded-full">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="mb-2 text-xl font-bold text-gray-900">Payment Successful!</h3>
                <p className="mb-4 text-sm text-gray-600">
                  The policy has been issued successfully. Transaction ID: {transactionId}
                </p>
                <button
                  onClick={() => {
                    setShowSuccess(false);
                    navigate("/dashboard");
                  }}
                  className="px-6 py-2 font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
                >
                  Continue
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Modal */}
      <AnimatePresence>
        {showError && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 z-50 justify-center items-center p-4 backdrop-blur-sm bg-black/50"
            onClick={() => {
              setShowError(false);
              setErrorMessage("");
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="p-6 w-full max-w-md bg-white rounded-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-red-100 rounded-full">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h3 className="mb-2 text-xl font-bold text-gray-900">Payment Failed</h3>
                <p className="mb-4 text-sm text-gray-600">{errorMessage}</p>
                <button
                  onClick={() => {
                    setShowError(false);
                    setErrorMessage("");
                  }}
                  className="px-6 py-2 font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
